/**
 * Route guard utilities for the Inventory System
 */

/**
 * Check if user has required role for a route
 * @param {string} userRole - Current user's role
 * @param {string} requiredRole - Required role for the route
 * @returns {boolean} - Whether user has access
 */
export function hasRequiredRole(userRole, requiredRole) {
  if (!requiredRole) return true // No role requirement
  return userRole === requiredRole
}

/**
 * Get default redirect route based on user role
 * @param {string} role - User's role
 * @returns {Object} - Route object for redirection
 */
export function getDefaultRoute(role) {
  switch (role) {
    case 'OWNER':
      return { name: 'Dashboard' }
    case 'CASHIER':
      return { name: 'SaleScanner' }
    default:
      return { name: 'SaleScanner' } // Fallback
  }
}

/**
 * Get accessible routes for a user role
 * @param {string} role - User's role
 * @returns {Array} - List of accessible route names
 */
export function getAccessibleRoutes(role) {
  const commonRoutes = ['SaleScanner', 'BarcodeScannerTest']
  
  const roleRoutes = {
    OWNER: [
      'Dashboard',
      'ProductList',
      'ProductCreate', 
      'ProductEdit',
      'SalesHistory',
      ...commonRoutes
    ],
    CASHIER: commonRoutes
  }
  
  return roleRoutes[role] || commonRoutes
}

/**
 * Check if a route is accessible for the user
 * @param {string} routeName - Name of the route
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether route is accessible
 */
export function isRouteAccessible(routeName, userRole) {
  const accessibleRoutes = getAccessibleRoutes(userRole)
  return accessibleRoutes.includes(routeName)
}

/**
 * Route metadata helpers
 */
export const ROUTE_PERMISSIONS = {
  // Owner-only routes
  OWNER_ONLY: [
    'Dashboard',
    'ProductList', 
    'ProductCreate',
    'ProductEdit',
    'SalesHistory'
  ],
  
  // Routes accessible to all authenticated users
  ALL_AUTHENTICATED: [
    'SaleScanner',
    'BarcodeScannerTest'
  ],
  
  // Guest-only routes
  GUEST_ONLY: [
    'Login'
  ]
}

/**
 * Get permission level for a route
 * @param {string} routeName - Name of the route
 * @returns {string} - Permission level ('owner', 'authenticated', 'guest')
 */
export function getRoutePermissionLevel(routeName) {
  if (ROUTE_PERMISSIONS.OWNER_ONLY.includes(routeName)) {
    return 'owner'
  }
  if (ROUTE_PERMISSIONS.ALL_AUTHENTICATED.includes(routeName)) {
    return 'authenticated'
  }
  if (ROUTE_PERMISSIONS.GUEST_ONLY.includes(routeName)) {
    return 'guest'
  }
  return 'public'
}

/**
 * Navigation helpers for components
 */
export function createNavigation(router, authStore) {
  return {
    /**
     * Navigate to route with proper access checking
     */
    navigateTo(routeName, params = {}) {
      if (!authStore.isAuthenticated && routeName !== 'Login') {
        router.push({ name: 'Login' })
        return
      }
      
      if (!isRouteAccessible(routeName, authStore.user?.role)) {
        router.push(getDefaultRoute(authStore.user?.role))
        return
      }
      
      router.push({ name: routeName, params })
    },
    
    /**
     * Get navigation menu items based on user role
     */
    getMenuItems() {
      if (!authStore.isAuthenticated) return []
      
      const allItems = [
        {
          name: 'Dashboard',
          label: 'Dashboard',
          icon: '📊',
          role: 'OWNER'
        },
        {
          name: 'ProductList',
          label: 'Products',
          icon: '📦',
          role: 'OWNER'
        },
        {
          name: 'SaleScanner',
          label: 'Scanner',
          icon: '📱',
          role: null // Accessible to all
        },
        {
          name: 'BarcodeScannerTest',
          label: 'Scanner Test',
          icon: '🔍',
          role: null // Accessible to all
        },
        {
          name: 'SalesHistory',
          label: 'Sales History',
          icon: '📋',
          role: 'OWNER'
        }
      ]
      
      return allItems.filter(item => 
        !item.role || hasRequiredRole(authStore.user?.role, item.role)
      )
    }
  }
} 